<template>
	<div>
		<div class="table_head table_head_filter">
			<md-switch v-model="showAllColumns">
				{{$t("common.table.switch.showAllColumns")}}
			</md-switch>
		</div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<ag-item :rowData="revolvingOptionsList"
						 :columnDefs="columnDefs"
						 v-on:sortChanged="sortChanged"
						 :with-button-group="true"
						 :with-tabs="true"
						 :with-header="true"
						 :loading-warning-text="accountsWarningText"
						 :key="accountsWarningText"
						 ref="exchangePutOptionsTable"
				></ag-item>
				<div class="flex flex_between">
					<ExchangeRevolvingPutOptionsLegend/>
					<Pagination
						:totalNum="totalNum"
						:pageSize="pageSize"
						:sizeChange="sizeChange"
						:currentChange="currentChange"
						:currentPage="page"
					></Pagination>
				</div>
			</div>
			<ExchangeRevolvingPutOptionsFilter :rating-config="ratingConfig" @apply-filter="applyFilter"/>
		</div>
	</div>
</template>

<script>
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import {revolvingPutOptionsBuyerApi} from "@/common/api/revolvingPutOptionsBuyerApi";
import {
  asDecimal,
  asIndustryText,
  asIsOrNotAvailableIfOtherCellIsZero,
  asLocalizedDate,
  asLocalizedNumberWithDays,
  asMoneyAmount
} from "@/components/templates/agGrid/formatter/valueFormatters";
import {enumDef} from "@/common/enumDefs";
import ExchangeRevolvingPutOptionsFilter
	from "@/components/templates/investor/exchange/revolving/ExchangeRevolvingPutOptionsFilter";
import ExchangeRevolvingPutOptionsLegend
	from "@/components/templates/investor/exchange/revolving/ExchangeRevolvingPutOptionsLegend";
import PaginationParent from "@/components/templates/common/PaginationParent";
import Pagination from "@/components/templates/common/Pagination";

export default {
	extends: PaginationParent,
	components: {
		ExchangeRevolvingPutOptionsFilter,
		ExchangeRevolvingPutOptionsLegend,
		AgItem,
		Pagination,
	},
	props: {
		activeAccount: { type: Object, default: null },
		accountsWarningText: { type: String, default: "" },
		ratingConfig: { type: Object, default: null },
	},
	data() {
		return {
			revolvingOptionsList: null,
			filters: [],
			sorting: [],
			columnDefs: [],
			showAllColumns: false,
		};
	},
	computed: {
		activeAccountId() {
			return this.activeAccount.id;
		},
	},
	beforeMount() {
		if (!this.activeAccount) {
			return;
		}

		this.setColumnDefs();
		this.getList();
	},
	methods: {
		setColumnDefs() {
			const cellClass = params => {
				if (!params.data.offerId) {
					return "";
				}

				if (params.data.offerAccountId !== this.activeAccountId) {
					return "active_revolving_offer_other_account_row";
				} else if (params.data.premium) {
					return "active_revolving_offer_row";
				}
			};
			this.columnDefs = [
				{
					headerName: this.$t("common.debtor"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.debtor.tooltip"),
					},
					children: [
						{
							headerName: this.$t("common.name"),
							field: "debtorName",
							minWidth: 175,
							nameType: "debtorName",
							idType: "debtorId",
							cellRendererFramework: "CompanyTem",
							sortable: true
						},
						{
							headerName: this.$t("common.cofaceDra"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.debtor.dra.tooltip"),
							},
							field: "debtorCofaceDra",
							minWidth: 70,
							cellRendererFramework: "CofaceDraRenderer",
							cellRendererParams: {
								draStatusField: "debtorCofaceDraStatus"
							},
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating1,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating1
								: this.$t("investor.exchange.table.header.customRating1"),
							field: "debtorRating1",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating2,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating2
								: this.$t("investor.exchange.table.header.customRating2"),
							field: "debtorRating2",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating3,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating3
								: this.$t("investor.exchange.table.header.customRating3"),
							field: "debtorRating3",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							headerName: this.$t("investor.exchange.table.header.industry"),
							field: "debtorIndustryId",
							minWidth: 250,
							valueFormatter: asIndustryText(),
							cellRendererFramework: "GeneralShortenedDisplay",
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.country"),
							field: "debtorCountry",
							minWidth: 90,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
					]
				},
				{
					headerName: this.$t("common.supplier"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.supplier.tooltip"),
					},
					children: [
						{
							headerName: this.$t("common.name"),
							minWidth: 175,
							field: "sellerName",
							nameType: "sellerName",
							idType: "sellerId",
							cellRendererFramework: "CompanyTem",
							sortable: true
						},
						{
							headerName: this.$t("common.cofaceDra"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.seller.dra.tooltip"),
							},
							field: "sellerCofaceDra",
							minWidth: 70,
							cellRendererFramework: "CofaceDraRenderer",
							cellRendererParams: {
								draStatusField: "sellerCofaceDraStatus"
							},
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating1,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating1
								: this.$t("investor.exchange.table.header.customRating1"),
							field: "sellerRating1",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating2,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating2
								: this.$t("investor.exchange.table.header.customRating2"),
							field: "sellerRating2",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating3,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating3
								: this.$t("investor.exchange.table.header.customRating3"),
							field: "sellerRating3",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.industry"),
							field: "sellerIndustryId",
							minWidth: 250,
							valueFormatter: asIndustryText(),
							cellRendererFramework: "GeneralShortenedDisplay",
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.country"),
							field: "sellerCountry",
							minWidth: 90,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.riskAnalysis"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.riskAnalysis.tooltip"),
					},
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.quantity"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.quantity.tooltip"),
							},
							headerClass: "align-right",
							field: "assessmentQuantity",
							cellStyle: {'justify-content': 'flex-end'},
							minWidth: 80,
							maxWidth: 100,
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays.tooltip"),
							},
							headerClass: "align-right",
							field: "assessmentLatePaymentDays",
							cellStyle: {'justify-content': 'flex-end'},
              valueFormatter: asIsOrNotAvailableIfOtherCellIsZero("assessmentQuantity"),
							minWidth: 80,
							maxWidth: 100,
							sortable: true
						},
            {
              headerName: this.$t("investor.exchange.table.header.riskAnalysis.currentOutstanding"),
              headerComponentParams: {
                tooltip: this.$t("investor.exchange.table.header.riskAnalysis.currentOutstanding.tooltip"),
              },
              headerClass: "align-right",
              field: "assessmentOverdueOutstanding",
              cellStyle: { "justify-content": "flex-end" },
              valueFormatter: asIsOrNotAvailableIfOtherCellIsZero("assessmentQuantity"),
              minWidth: 120,
              maxWidth: 120,
              columnGroupShow: "open",
              sortable: true
            },
            {
              headerName: this.$t("investor.exchange.table.header.riskAnalysis.directPay"),
              headerComponentParams: {
                tooltip: this.$t("investor.exchange.table.header.riskAnalysis.directPay.tooltip"),
              },
              headerClass: "align-right",
              field: "assessmentDirectPay",
              cellStyle: { "justify-content": "flex-end" },
              minWidth: 100,
              maxWidth: 100,
              cellRendererFramework: "BooleanAsIcon",
              columnGroupShow: "open",
              sortable: true
            }
					]
				},
				{
					headerName: this.$t("common.revolvingPutOption"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.revolvingPutOption.tooltip"),
					},
					children: [
						{
							headerName: this.$t("revolvingPutOption.optionStart"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.optionStart.tooltip"),
							},
							minWidth: 120,
							field: "startDate",
							valueFormatter: asLocalizedDate(),
							sortable: true
						},
						{
							headerName: this.$t("revolvingPutOption.optionEnd"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.optionEnd.tooltip"),
							},
							minWidth: 120,
							field: "endDate",
							valueFormatter: asLocalizedDate(),
							sortable: true
						},
						{
							headerName: this.$t("revolvingPutOption.discount"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.discount.tooltip"),
							},
							headerClass: "align-right",
							minWidth: 150,
							field: "discountRate",
							cellStyle: {'justify-content': 'flex-end'},
							valueFormatter: asDecimal({ showPercentSign: true }),
							sortable: true
						},
						{
							headerName: this.$t("revolvingPutOption.revolvingLine"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.revolvingLine.tooltip"),
							},
							headerClass: "align-right",
							minWidth: 150,
							field: "saleLimit",
							cellStyle: {'justify-content': 'flex-end'},
							valueFormatter: asMoneyAmount(),
							sortable: true
						},
						{
							headerName: this.$t("revolvingPutOption.estimatedSalesVolume"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.estimatedSalesVolume.tooltip"),
							},
							headerClass: "align-right",
							minWidth: 225,
							field: "estimatedSalesVolume",
							cellStyle: {'justify-content': 'flex-end'},
							valueFormatter: asMoneyAmount(),
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.applicableLaw.short"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.applicableLaw.tooltip"),
							},
							minWidth: 110,
							field: "applicableLaw",
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("revolvingPutOption.paymentTerms"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.paymentTerms.tooltip"),
							},
							minWidth: 150,
							field: "paymentTerms",
							valueFormatter: asLocalizedNumberWithDays(),
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.assignment"),
							headerComponentParams: {
								tooltip: this.$t("common.disclosed.tooltip"),
							},
							minWidth: 150,
							field: "disclosed",
							cellRendererFramework: "EnumAsText",
							enumDef: enumDef.disclosedType,
							columnGroupShow: "open",
							sortable: true
						},
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.revolvingOffer"),
					headerClass: "bg_special",
					children: [
						{
							headerName: this.$t("revolvingPutOption.premium.withPercent"),
							headerComponentParams: {
								tooltip: this.$t("revolvingPutOption.table.header.premium.tooltip"),
							},
							field: "premiumInput",
							minWidth: 225,
							cellRendererFramework: "PremiumAmount",
							cellRendererParams: {
								premiumField: "premium",
								makeOffer: this.confirmMakeOffer,
								editOffer: this.confirmEditOffer,
							},
							pinned: "right",
						},
						{
							headerName: this.$t("investor.exchange.table.header.timeLeft"),
							field: "publishingIntervalEndTime",
							minWidth: 100,
							cellRendererFramework: "TimeLeftCountdown",
							cellRendererParams: {
								expiredTextGetter: (data) => data.offerId
									? this.$t("revolvingPutOption.offer.waitingToBeAccepted")
									: this.$t("common.countdown.expired"),
								onExpire: this.optionExpired
							},
							sortable: true,
							sort: "asc",
							pinned: "right"
						},
					]
				},
			];
			this.columnDefs.forEach(col => {
				if (col.children) {
					col.children.forEach(childCol => (childCol.cellClass = cellClass));
				} else {
					col.cellClass = cellClass;
				}
			});
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		getList() {
			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting
			};

			return new Promise((resolve, reject) => {
				revolvingPutOptionsBuyerApi.inspection.listExchange(this.activeAccountId, postData)
					.then((res) => {
						this.setTotalNum(res.recordsTotal);
						this.revolvingOptionsList = res.data.map(item => {
							return {
								...item,
								expired: false,
								// Make sure data for input field cell renderers is initialized before the table renders its data
								premiumInput: item.premium || item.premiumInput || null,
							};
						});
						resolve();
					})
					.catch((e) => {
						this.toast(e);
						reject();
					});
			});
		},
		confirmMakeOffer(data) {
			let option = {
				title: this.$t("investor.exchange.table.operation.makeOffer.confirm.title"),
				msg: this.$t("investor.exchange.table.operation.makeOffer.confirm.msg"),
				confirmFun: this.makeOffer,
				params: data
			};
			this.confirmMeg(option, true);
		},
		makeOffer(data) {
			let postData = {
				optionId: data.optionId,
				accountId: this.activeAccountId,
				premium: data.premiumInput
			};

			revolvingPutOptionsBuyerApi.operation.makeOffer(postData)
				.then(() => {
					this.toastSuccess();
					this.getList();
				})
				.catch(this.toast);
		},
		confirmEditOffer(data) {
			let option = {
				title: this.$t("investor.exchange.table.operation.editOffer.confirm.title"),
				msg: this.$t("investor.exchange.table.operation.editOffer.confirm.msg"),
				confirmFun: this.editOffer,
				params: data
			};
			this.confirmMeg(option, true);
		},
		editOffer(data) {
			let postData = {
				optionId: data.optionId,
				accountId: this.activeAccountId,
				premium: data.premiumInput
			};

			revolvingPutOptionsBuyerApi.operation.editOffer(data.offerId, postData)
				.then(() => {
					this.toastSuccess();
					this.getList();
				})
				.catch(this.toast);
		},
		optionExpired(data) {
			data.expired = true;
		},
		applyFilter(filters) {
			this.filters = filters;
			this.getList();
		},
	},
	watch: {
		showAllColumns() {
			this.$refs.exchangePutOptionsTable.showAllColumns(this.showAllColumns);
		},
		ratingConfig() {
			this.setColumnDefs();
		},
		activeAccount() {
			this.getList();
		}
	},
};
</script>

<style lang="scss" scoped>
@import "src/components/templates/investor/exchange/exchange";
</style>
